// client/src/pages/ShoppingCart.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import BackButton from '../../components/customs/backButton';
import showNotification from '../../utilities/showNotification';
import { GlobalContext } from '../../context/provider';
import useStudent from '../../hooks/useStudent';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './shoppingCarts.css';

const planConfig = {
    monthly_3:  { months: 3,  multiplier: 1.05 },
    monthly_6:  { months: 6,  multiplier: 1.15 },
    monthly_12: { months: 12, multiplier: 1.25 }
};

const ShoppingCart = () => {
    const navigate = useNavigate();
    const { 
        authState: { authData }
    } = useContext(GlobalContext);
    
    const { 
        getCart,
        removeFromCart,
        processPayment,
        getPaymentMethods,  
        createStripeCheckout,
        createSubscription,  // ← ADD THIS
        addPaymentMethod
    } = useStudent();

    const {
        getCourseById,
        validateCoupon
    } = useCourse();

    const [isLoading, setIsLoading] = useState(true);
    const [cartItems, setCartItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [cartSummary, setCartSummary] = useState({
        totalAmount: 0,
        currency: 'USD',
        itemCount: 0
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('credit_card');

    // Coupon state
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [isCouponLoading, setIsCouponLoading] = useState(false);

    // Subscription state
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [selectedCourseForSubscription, setSelectedCourseForSubscription] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // Saved payment methods
    const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    useEffect(() => {
        loadCartData();
    }, []);

    const loadCartData = async () => {
        setIsLoading(true);
        try {
            const response = await getCart(authData.id);
            
            if (response.success && response.data) {
                // Fetch full course details for each cart item
                const itemsWithDetails = await Promise.all(
                    response.data.items.map(async (item) => {
                        const courseResponse = await getCourseById(item.courseId);
                        return {
                            ...item,
                            courseDetails: courseResponse.success ? courseResponse.data : null
                        };
                    })
                );
                
                setCartItems(itemsWithDetails);
                setCartSummary({
                    totalAmount: response.data.totalAmount,
                    currency: response.data.currency,
                    itemCount: response.data.itemCount
                });
                
                // Select all items by default
                setSelectedItems(new Set(itemsWithDetails.map(item => item.courseId)));
            } else {
                setCartItems([]);
            }
        } catch (error) {
            console.error('Failed to load cart:', error);
            showNotification('Failed to load cart', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectItem = (courseId) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(courseId)) {
                newSet.delete(courseId);
            } else {
                newSet.add(courseId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedItems.size === cartItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(cartItems.map(item => item.courseId)));
        }
    };

    const handleRemoveItem = async (courseId) => {
        try {
            const response = await removeFromCart(authData.id, courseId);
            
            if (response.success) {
                window.dispatchEvent(new Event('cartUpdated'));
                showNotification('Course removed from cart', 'success');
                await loadCartData();
                
                // Remove from selected items if it was selected
                setSelectedItems(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(courseId);
                    return newSet;
                });
            } else {
                showNotification('Failed to remove course', 'error');
            }
        } catch (error) {
            console.error('Remove error:', error);
            showNotification('Failed to remove course', 'error');
        }
    };

    const calculateSelectedTotal = () => {
        const total = cartItems
            .filter(item => selectedItems.has(item.courseId))
            .reduce((sum, item) => sum + item.price, 0);
        
        // Apply coupon discount if exists
        if (appliedCoupon) {
            return appliedCoupon.newTotal;
        }
        
        return total;
    };

    const handleProceedToPayment = () => {
        if (selectedItems.size === 0) {
            showNotification('Please select at least one course', 'error');
            return;
        }
        setShowPaymentModal(true);
    };

    const handlePayment = async () => {
        if (selectedItems.size === 0) {
            showNotification('Please select at least one course', 'error');
            return;
        }

        setIsProcessing(true);
        try {
            const paymentData = {
                courseIds: Array.from(selectedItems),
                paymentMethod: paymentMethod,
                transactionId: `TXN${Date.now()}` // Generate transaction ID
            };

            const response = await processPayment(authData.id, paymentData);
            
            if (response.success) {
                showNotification('Payment successful! You are now enrolled in the courses.', 'success');
                setShowPaymentModal(false);
                
                // Redirect to courses page after 2 seconds
                setTimeout(() => {
                    navigate('/courses');
                }, 2000);
            } else {
                showNotification(response.message || 'Payment failed', 'error');
            }
        } catch (error) {
            console.error('Payment error:', error);
            showNotification('Payment processing failed', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    // Handle coupon validation
    const handleValidateCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponError('Please enter a coupon code');
            return;
        }

        // Get courseId from cart (first item)
        const courseId = cartItems[0]?.courseId;
        
        if (!courseId) {
            setCouponError('No course in cart');
            return;
        }

        setIsCouponLoading(true);
        setCouponError('');

        try {
            // Use existing validateCoupon from useCourse
            const response = await validateCoupon(courseId, couponCode.toUpperCase());
            
            if (response.success && response.data?.valid) {
                // Calculate discount for display
                const coupon = response.data.coupon;
                const cartTotal = cartItems.reduce((sum, item) => sum + item.price, 0);
                
                let discountAmount = 0;
                if (coupon.discountType === 'percentage') {
                    discountAmount = (cartTotal * coupon.discountValue) / 100;
                } else if (coupon.discountType === 'fixed') {
                    discountAmount = coupon.discountValue;
                }
                
                const newTotal = Math.max(0, cartTotal - discountAmount);
                
                setAppliedCoupon({
                    originalTotal: cartTotal,
                    discountAmount,
                    newTotal,
                    coupon: {
                        code: coupon.couponCode,
                        discountType: coupon.discountType,
                        discountValue: coupon.discountValue
                    }
                });
                
                showNotification(
                    `Coupon applied! You save ${coupon.discountType === 'percentage' 
                        ? `${coupon.discountValue}%` 
                        : `$${coupon.discountValue}`}`, 
                    'success'
                );
            } else {
                setCouponError(response.data?.error || response.error || 'Invalid coupon code');
            }
        } catch (error) {
            setCouponError(error.message || 'Failed to validate coupon');
        } finally {
            setIsCouponLoading(false);
        }
    };

    // const handleValidateCoupon = async () => {
    // };

    // Remove coupon
    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponError('');
        showNotification('Coupon removed', 'info');
    };

    // Handle Stripe Checkout
    const handleStripeCheckout = async () => {
        if (selectedItems.size === 0) {
            showNotification('Please select at least one course', 'error');
            return;
        }

        setIsProcessing(true);
        try {
            const checkoutData = {
                successUrl: `${window.location.origin}/payment-success`,
                cancelUrl: `${window.location.origin}/cart`,
                couponCode: appliedCoupon?.coupon?.code || null
            };

            const response = await createStripeCheckout(authData.id, checkoutData);
            
            if (response.success) {
                showNotification('Redirecting to payment...', 'info');
            } else {
                showNotification(response.message || 'Checkout failed', 'error');
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            showNotification('Checkout failed', 'error');
            setIsProcessing(false);
        }
    };

    // Open subscription modal
    const handleOpenSubscriptionModal = (courseId) => {
        const course = cartItems.find(item => item.courseId === courseId);
        if (course) {
            if (!course.courseDetails?.paymentOptions?.allowInstallments) {
                showNotification('This course does not support installment payments', 'error');
                return;
            }
            setSelectedCourseForSubscription(course);
            setSelectedPlan(null); // ← ADD THIS - Reset plan selection
            setShowSubscriptionModal(true);
        }
    };

    // Handle subscription
    const handleSubscribe = async () => {
        if (!selectedPlan) {
            showNotification('Please select a payment plan', 'error');
            return;
        }

        if (!selectedCourseForSubscription) {
            showNotification('No course selected', 'error');
            return;
        }

        setIsProcessing(true);
        try {
            const subscriptionData = {
                courseId: selectedCourseForSubscription.courseId,
                planType: selectedPlan,
                couponCode: appliedCoupon?.coupon?.code || undefined
            };

            const response = await createSubscription(authData.id, subscriptionData);
            
            if (response.success && response.data) {
                showNotification('Subscription created! Redirecting to payment...', 'success');
                
                // Redirect to Stripe payment for first installment
                if (response.data.url) {
                    window.location.href = response.data.url;
                } else if (response.data.clientSecret) {
                    // Handle card payment confirmation
                    // You'll need to integrate Stripe Elements here
                    showNotification('Payment setup required', 'info');
                }
                
                setShowSubscriptionModal(false);
            } else {
                showNotification(response.message || 'Failed to create subscription', 'error');
            }
        } catch (error) {
            console.error('Subscription error:', error);
            showNotification('Subscription failed', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    // Load saved payment methods
    const loadPaymentMethods = async () => {
        try {
            const response = await getPaymentMethods(authData.id);
            if (response.success) {
                setSavedPaymentMethods(response.data);
            }
        } catch (error) {
            console.error('Failed to load payment methods:', error);
        }
    };

    return (
        <div className="main-container">
            <div className="main-content">
                
                {/* Header */}
                <div className="header-row">
                    <ProfileHeader 
                        data={authData}
                        title="🛒 Shopping Cart"
                        activePage="studentdashboard" 
                    />
                    <div className="headerMiddle">
                        <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
                    </div>
                    <SharedHeader 
                        notificationCount={0} 
                        icons={[]} 
                    />
                </div>

                {/* Cart Content */}
                <div className="shoppingCart-scrollable-list">
                    {isLoading ? (
                        <div className="loading-container">
                            <div className="loader"></div>
                            <p>Loading cart...</p>
                        </div>
                    ) : cartItems.length === 0 ? (
                        <div className="empty-cart">
                            <div className="empty-cart-icon">🛒</div>
                            <h2>Your cart is empty</h2>
                            <p>Browse our courses and add them to your cart</p>
                            <button 
                                onClick={() => navigate('/courses')}
                                className="btn-browse-courses"
                            >
                                Browse Courses
                            </button>
                        </div>
                    ) : (
                        <div className="cart-layout">
                            <div className="cart-info-box">
                                <h4>📚 Benefits</h4>
                                <ul>
                                    <li>✓ Certificate upon completion</li>
                                    <li>✓ Instructor support</li>
                                    <li>✓ Interactive labs & quizzes</li>
                                </ul>
                            </div>

                            {/* Coupon Section */}
                            <div className="coupon-section">
                                <h4>Have a coupon?</h4>
                                <div className="coupon-input-group">
                                    <input
                                        type="text"
                                        placeholder="Enter code"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                        disabled={appliedCoupon || isCouponLoading}
                                    />
                                    {!appliedCoupon ? (
                                        <button
                                            onClick={handleValidateCoupon}
                                            disabled={!couponCode.trim() || isCouponLoading}
                                            className="btn-apply-coupon"
                                        >
                                            {isCouponLoading ? '...' : 'Apply'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleRemoveCoupon}
                                            className="btn-remove-coupon"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                                {couponError && (
                                    <p className="coupon-error">{couponError}</p>
                                )}
                                {appliedCoupon && (
                                    <p className="coupon-success">
                                        ✓ Saved {cartSummary.currency} {appliedCoupon.discountAmount.toFixed(2)}
                                    </p>
                                )}
                            </div>

                            {/* Cart Items Section */}
                            <div className="cart-items-section">
                                <div className="cart-header">
                                    <h2>Shopping Cart ({cartItems.length} items)</h2>
                                    <label className="select-all-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.size === cartItems.length && cartItems.length > 0}
                                            onChange={handleSelectAll}
                                            className="select-checkbox"
                                        />
                                        <span>Select All</span>
                                    </label>
                                </div>

                                <div className="cart-items-list">
                                    {cartItems.map((item, idx) => (
                                        <div key={idx} className="cart-item-card">
                                            <div className="cart-item-select">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.has(item.courseId)}
                                                    onChange={() => handleSelectItem(item.courseId)}
                                                    className="item-checkbox"
                                                />
                                            </div>

                                            <div className="cart-item-image">
                                                {item.courseDetails?.imageUrl ? (
                                                    <img 
                                                        src={item.courseDetails.imageUrl} 
                                                        alt={item.courseDetails?.title}
                                                        onError={(e) => {
                                                            e.target.src = '/images/course-placeholder.png';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="placeholder-image">
                                                        📚
                                                    </div>
                                                )}
                                            </div>

                                            <div className="cart-item-details">
                                                <h3 className="cart-item-title">
                                                    {item.courseDetails?.title || 'Course Title'}
                                                </h3>
                                                <p className="cart-item-description">
                                                    {item.courseDetails?.shortDescription || 'No description available'}
                                                </p>
                                                <div className="cart-item-meta">
                                                    <span className="meta-badge">
                                                        {item.courseDetails?.level || 'N/A'}
                                                    </span>
                                                    <span className="meta-badge">
                                                        {item.courseDetails?.category || 'N/A'}
                                                    </span>
                                                    <span className="meta-text">
                                                        👨‍🏫 {item.courseDetails?.instructorName || 'TBA'}
                                                    </span>
                                                </div>
                                                {/* Only show installment button if allowed */}
                                                {item.courseDetails?.paymentOptions?.allowInstallments && (
                                                    <button
                                                        onClick={() => handleOpenSubscriptionModal(item.courseId)}
                                                        className="btn-subscription-option"
                                                    >
                                                        💳 Pay in Installments
                                                    </button>
                                                )}
                                            </div>

                                            <div className="cart-item-price">
                                                <div className="price-display">
                                                    <span className="currency">{item.currency}</span>
                                                    <span className="amount">{item.price.toFixed(2)}</span>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveItem(item.courseId)}
                                                    className="btn-remove"
                                                >
                                                    🗑️ Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Cart Summary Section */}
                            <div className="cart-summary-card">
                                <h3>Order Summary</h3>
                                <div className="summary-row">
                                    <span>Items Selected:</span>
                                    <span className="summary-value">{selectedItems.size}</span>
                                </div>

                                <div className="summary-row">
                                    <span>Subtotal:</span>
                                    <span className="summary-value">
                                        {cartSummary.currency} {calculateSelectedTotal().toFixed(2)}
                                    </span>
                                </div>

                                <div className="summary-divider"></div>

                                {appliedCoupon && (
                                    <div className="summary-row discount-row">
                                        <span>Discount ({appliedCoupon.coupon.code}):</span>
                                        <span className="summary-discount">
                                            -{cartSummary.currency} {appliedCoupon.discountAmount.toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                <div className="summary-row total-row">
                                    <span>Total:</span>
                                    <span className="summary-total">
                                        {cartSummary.currency} {calculateSelectedTotal().toFixed(2)}
                                    </span>
                                </div>
                                
                                <div>
                                    <button
                                        onClick={handleProceedToPayment}
                                        className="btn-checkout"
                                        disabled={selectedItems.size === 0}
                                    >
                                        💳 Proceed to Payment
                                    </button>

                                    <button
                                        onClick={() => navigate('/courses')}
                                        className="btn-continue-shopping"
                                    >
                                        ← Continue Shopping
                                    </button>
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="modal-content-top payment-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-top">
                            <h2>💳 Check Out</h2>
                            <button onClick={() => setShowPaymentModal(false)} className="close-button">✕</button>
                        </div>

                        <div className="modal-body">
                            <div className="payment-summary">
                                <h3>Order Summary</h3>
                                <div className="payment-items">
                                    {cartItems
                                        .filter(item => selectedItems.has(item.courseId))
                                        .map((item, idx) => (
                                            <div key={idx} className="payment-item">
                                                <span>{item.courseDetails?.title}</span>
                                                <span>{item.currency} {item.price.toFixed(2)}</span>
                                            </div>
                                        ))}
                                </div>
                                
                                {appliedCoupon && (
                                    <div className="payment-discount">
                                        <span>Discount ({appliedCoupon.coupon.code}):</span>
                                        <span>-{cartSummary.currency} {appliedCoupon.discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                
                                <div className="payment-total">
                                    <strong>Total:</strong>
                                    <strong>{cartSummary.currency} {calculateSelectedTotal().toFixed(2)}</strong>
                                </div>
                            </div>

                            <div className="stripe-checkout-section">
                                <p className="checkout-info">
                                    You will be redirected to Stripe's secure checkout page
                                </p>
                                <button
                                    onClick={handleStripeCheckout}
                                    className="btn-stripe-checkout"
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? '⏳ Redirecting...' : '🔒 Checkout with Stripe'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Modal */}
            {showSubscriptionModal && selectedCourseForSubscription && (
                <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
                    <div className="modal-content-top subscription-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-top">
                            <h2>💳 Choose Payment Plan</h2>
                            <button onClick={() => setShowSubscriptionModal(false)} className="close-button">✕</button>
                        </div>

                        <div className="modal-body">
                            <div className="subscription-course-info">
                                <h3>{selectedCourseForSubscription.courseDetails?.title}</h3>
                                <p className="course-total">
                                    Total: {selectedCourseForSubscription.currency}{' '}
                                    {appliedCoupon
                                        ? Math.max(0, selectedCourseForSubscription.price - appliedCoupon.discountAmount).toFixed(2)
                                        : selectedCourseForSubscription.price.toFixed(2)
                                    }
                                </p>
                                {appliedCoupon && (
                                <p style={{ color: '#2d7a3a', fontSize: '13px', marginTop: '4px' }}>
                                    ✓ Coupon applied — saving {cartSummary.currency} {appliedCoupon.discountAmount.toFixed(2)}
                                </p>
                                )}
                            </div>

                            <div className="subscription-plans">
                                {[
                                    { value: 'monthly_3', label: '3 Monthly Payments', months: 3 },
                                    { value: 'monthly_6', label: '6 Monthly Payments', months: 6 },
                                    { value: 'monthly_12', label: '12 Monthly Payments', months: 12 }
                                ].filter(plan => 
                                    selectedCourseForSubscription.courseDetails?.paymentOptions?.installmentPlans?.includes(plan.value)
                                ).map(plan => (
                                    <label key={plan.value} className="subscription-plan-option">
                                        <input 
                                            type="radio" 
                                            name="plan" 
                                            value={plan.value}
                                            checked={selectedPlan === plan.value}
                                            onChange={() => setSelectedPlan(plan.value)}
                                        />
                                        <div className="plan-details">
                                            <span className="plan-name">{plan.label}</span>
                                            <span className="plan-price">
                                                {selectedCourseForSubscription.currency} 
                                                {/* Current — no coupon applied */}
                                                {/* {(selectedCourseForSubscription.price / plan.months).toFixed(2)}/month */}
                                                {/* Apply coupon discount before dividing */}
                                                {(() => {
                                                const { months, multiplier } = planConfig[plan.value];
                                                const basePrice = selectedCourseForSubscription.price;
                                                let discountedPrice = basePrice;
                                                if (appliedCoupon) {
                                                    if (appliedCoupon.coupon.discountType === 'percentage') {
                                                    discountedPrice = basePrice * (1 - appliedCoupon.coupon.discountValue / 100);
                                                    } else {
                                                    discountedPrice = Math.max(0, basePrice - appliedCoupon.coupon.discountValue);
                                                    }
                                                }
                                                return ((discountedPrice / months) * multiplier).toFixed(2);
                                                })()}/month
                                            </span>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            <button 
                                className="btn-subscribe"
                                onClick={handleSubscribe}
                                disabled={!selectedPlan || isProcessing}
                            >
                                {isProcessing ? '⏳ Processing...' : '✅ Subscribe'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShoppingCart;